import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { api } from '../../../lib/api'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Button } from '../../../components/ui/button'
import { Input } from '../../../components/ui/input'
import FundUseDisclosure from '../../../components/giving/FundUseDisclosure'
import FeeDisclosure from '../../../components/giving/FeeDisclosure'
import { Loader2, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react'

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null

function RecurringDonationForm({ parishId, parishName }) {
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  const [amount, setAmount] = useState('')
  const [interval, setInterval] = useState('monthly')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null)
  const [loadingSetupIntent, setLoadingSetupIntent] = useState(true)

  useEffect(() => {
    // Create SetupIntent when component mounts
    const createSetupIntent = async () => {
      try {
        const response = await api.createSetupIntent(parishId)
        setSetupIntentClientSecret(response.clientSecret)
      } catch (err) {
        console.error('Failed to create SetupIntent:', err)
        setError(err.message || 'Failed to initialize payment form')
      } finally {
        setLoadingSetupIntent(false)
      }
    }

    createSetupIntent()
  }, [parishId])

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum < 0.5) {
      setError('Please enter a valid amount (minimum $0.50)')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      // Confirm SetupIntent to attach payment method
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      })

      if (confirmError) {
        setError(confirmError.message || 'Failed to save payment method')
        setSubmitting(false)
        return
      }

      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError('Failed to save payment method')
        setSubmitting(false)
        return
      }

      const paymentMethodId = setupIntent.payment_method

      // Create recurring donation
      const amountCents = Math.round(amountNum * 100)
      await api.createRecurringDonation({
        parishId,
        amountCents,
        interval,
        paymentMethodId,
      })

      // Navigate to success page or donations page
      navigate('/app/donor/donations?recurring=success')
    } catch (err) {
      console.error('Failed to create recurring donation:', err)
      setError(err.message || 'Failed to create recurring donation')
    } finally {
      setSubmitting(false)
    }
  }

  if (loadingSetupIntent) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-red-600 font-semibold mb-1">Error</h3>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">
          Donation Amount ($)
        </label>
        <Input
          type="number"
          step="0.01"
          min="0.5"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value)
            setError(null)
          }}
          placeholder="0.00"
          required
          className="bg-surface-linen border-surface-border text-charcoal"
        />
        <p className="text-xs text-charcoal-muted mt-1">
          Minimum donation: $0.50
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">
          Donation Frequency
        </label>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setInterval('weekly')}
            className={`px-4 py-3 rounded border-2 transition-colors ${
              interval === 'weekly'
                ? 'border-gold bg-gold/10 text-charcoal'
                : 'border-surface-border bg-surface-linen text-charcoal hover:border-gold/50'
            }`}
          >
            <div className="font-semibold">Weekly</div>
            <div className="text-xs text-charcoal-muted mt-1">
              Every week
            </div>
          </button>
          <button
            type="button"
            onClick={() => setInterval('monthly')}
            className={`px-4 py-3 rounded border-2 transition-colors ${
              interval === 'monthly'
                ? 'border-gold bg-gold/10 text-charcoal'
                : 'border-surface-border bg-surface-linen text-charcoal hover:border-gold/50'
            }`}
          >
            <div className="font-semibold">Monthly</div>
            <div className="text-xs text-charcoal-muted mt-1">
              Every month
            </div>
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-charcoal mb-2">
          Payment Method
        </label>
        <div className="bg-white border border-surface-border rounded p-4">
          <PaymentElement
            options={{
              layout: 'tabs',
            }}
          />
        </div>
        <p className="text-xs text-charcoal-muted mt-2">
          Your payment method will be securely saved for future donations.
        </p>
      </div>

      <div className="bg-gold/10 border border-gold/30 rounded p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
          <div className="text-sm text-charcoal/90">
            <p className="font-semibold mb-1">Recurring Donation Summary</p>
            <p className="text-charcoal-muted">
              You will be charged <strong>${amount || '0.00'}</strong> {interval === 'weekly' ? 'every week' : 'every month'} to{' '}
              <strong>{parishName}</strong>. You can cancel or modify this donation at any time from your donations page.
            </p>
          </div>
        </div>
      </div>

      <div className="flex gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/app/donor/donations')}
          className="flex-1 border-surface-border text-charcoal"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!stripe || submitting || !amount}
          className="flex-1 bg-gold text-black font-semibold hover:bg-gold/90"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Create Recurring Donation
            </>
          )}
        </Button>
      </div>
    </form>
  )
}

export default function RecurringDonationSetup() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [parish, setParish] = useState(null)
  const [error, setError] = useState(null)
  const [setupIntentClientSecret, setSetupIntentClientSecret] = useState(null)
  const [loadingSetupIntent, setLoadingSetupIntent] = useState(true)

  const parishId = searchParams.get('parishId')

  useEffect(() => {
    if (!parishId) {
      setError('Missing parish ID')
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        const parishData = await api.getParish(parishId)
        setParish(parishData)
        // Unavailable orgs cannot take gifts; the backend would reject the SetupIntent.
        if (parishData.giving?.mode !== 'unavailable') {
          const setupIntentResponse = await api.createSetupIntent(parishId)
          setSetupIntentClientSecret(setupIntentResponse.clientSecret)
        }
      } catch (err) {
        console.error('Failed to fetch data:', err)
        setError(err.message || 'Failed to load data')
      } finally {
        setLoading(false)
        setLoadingSetupIntent(false)
      }
    }

    fetchData()
  }, [parishId])

  if (!STRIPE_PUBLISHABLE_KEY) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <p className="text-red-600 text-sm">Stripe is not configured. Please contact support.</p>
        </div>
        <Button onClick={() => navigate('/app/donor/donations')}>
          Back to Donations
        </Button>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (error || !parish) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6 mb-4">
          <p className="text-red-600 text-sm">{error || 'Parish not found'}</p>
        </div>
        <Button onClick={() => navigate('/app/donor/donations')}>
          Back to Donations
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Page header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-gold/40 rounded flex items-center justify-center bg-gold/10">
          <RefreshCw className="w-5 h-5 text-gold" />
        </div>
        <div>
          <h1 className="text-2xl font-bold font-display text-charcoal leading-tight">
            Set Up Recurring Donation
          </h1>
          <p className="text-charcoal-muted text-sm">{parish.name}</p>
        </div>
      </div>

      {parish.giving?.mode === 'unavailable' ? (
        <div className="bg-white border border-surface-border rounded p-6">
          <p className="text-charcoal-muted">
            {parish.name} isn't accepting donations right now.
          </p>
        </div>
      ) : (
      <div className="bg-white border border-gold/30 rounded-lg overflow-hidden">
        {/* Gold accent bar */}
        <div className="h-1.5 bg-gold w-full" />
        <div className="p-6">
          {setupIntentClientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: setupIntentClientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#D4AF37',
                    colorText: '#1c1c1c',
                    colorDanger: '#ef4444',
                    fontFamily: 'system-ui, sans-serif',
                    spacingUnit: '4px',
                    borderRadius: '6px',
                  },
                },
              }}
            >
              <RecurringDonationForm
                parishId={parishId}
                parishName={parish.name}
              />
            </Elements>
          ) : (
            <div className="flex items-center justify-center min-h-[400px]">
              <Loader2 className="w-8 h-8 text-gold animate-spin" />
            </div>
          )}
          <FundUseDisclosure parishName={parish.name} fundUse={parish.giving?.fundUse} />
          <FeeDisclosure
            percent={parish.giving?.fee?.oneTimePercent}
            fixedCents={parish.giving?.fee?.fixedCents}
            parishName={parish.name}
          />
        </div>
      </div>
      )}
    </div>
  )
}
